import '@/styles/app.scss'
import React, { useEffect } from 'react'

import { enabledIn, supportedLanguages } from '@common'
import { AbstractIntlMessages, NextIntlProvider } from 'next-intl'
import { AppProps } from 'next/app'
import { useRouter } from 'next/dist/client/router'
import dynamic from 'next/dynamic'
import { Provider } from 'react-redux'

import { DefaultMetaTags } from '@/components/MetaTags'
import { setCreatorBoostCode } from '@/features/checkout/checkoutSlice'
import CookieConsent from '@/features/CookieConsent'
import ErrorBoundary from '@/features/ErrorBoundary'
import { Footer } from '@/features/Footer'
import { Header } from '@/features/header/Header'
import { useLocale } from '@/features/locale/useLocale'
import { Overlay } from '@/features/overlay/Overlay'
import Tracking from '@/features/Tracking'
import { useAuth } from '@/features/user/useAuth'
import { globalFormats } from '@/localization/globalFormats'
import { PreloadedState, useInitStore } from '@/store'
import logger from '@/utils/logger'
import 'intersection-observer'

import { config } from '../../config'
import { useResolveViewerCountry } from '../features/common/useResolveViewerCountry'
import { useSetCookieConsentLocale } from '../features/common/useSetCookieConsentLocale'
import { isClient } from '../utils/isClient'
import { useAppDispatch } from '../utils/useAppDispatch'

const { BUILD_ID, TRACKING_ENABLED, COOKIE_CONSENT_ENABLED, STAGE } = config

const EMPTY_OBJECT = {}

if (isClient && BUILD_ID) {
  logger.info(`Supercell Store\nBUILD ID: ${BUILD_ID}`)
}
const FeatureFlagMenuButton = dynamic<unknown>(() =>
  import('../features/featureFlags/FeatureFlags').then((mod) => mod.FeatureFlagMenuButton),
)

if (isClient) {
  window.SC_STORE_LOADED = false
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      // Next.js marks this after hydration is completed
      if (entry.name === 'afterHydrate') {
        window.SC_STORE_LOADED = true
      }
    }
  })
  observer.observe({ entryTypes: ['mark'] })
}

function App({
  Component,
  pageProps,
}: Pick<AppProps<{ initialState?: PreloadedState; messages: AbstractIntlMessages }>, 'Component' | 'pageProps'>) {
  const {
    pathname,
    query: { boost },
  } = useRouter()
  const locale = useLocale()
  const isReceiptPage = pathname === '/receipt'
  const isMaintenancePage = pathname === '/maintenance'
  const showHeader = !isReceiptPage && !isMaintenancePage
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (boost && typeof boost === 'string') {
      dispatch(setCreatorBoostCode({ isValid: true, value: boost, validating: true }))
    }
  }, [boost, dispatch])

  useSetCookieConsentLocale()
  useResolveViewerCountry()
  useAuth()
  return (
    <div id="main" dir={supportedLanguages[locale].rightToLeft ? 'rtl' : 'ltr'}>
      {enabledIn(STAGE, ['development', 'local-dev', 'staging', 'test']) && <FeatureFlagMenuButton />}
      {showHeader && <Header />}
      <Component {...pageProps} />
      <Overlay />
      <Footer />
    </div>
  )
}

function StoreApp({
  pageProps,
  Component,
}: AppProps<{ initialState?: PreloadedState; messages: AbstractIntlMessages }>) {
  const store = useInitStore(pageProps.initialState || EMPTY_OBJECT)
  return (
    <NextIntlProvider
      messages={pageProps.messages}
      defaultTranslationValues={globalFormats}
      onError={() => 'not_found'}
    >
      <ErrorBoundary sentryEnabled={enabledIn(STAGE, ['staging', 'production'])}>
        <Provider store={store}>
          <DefaultMetaTags />
          {COOKIE_CONSENT_ENABLED && <CookieConsent />}
          {TRACKING_ENABLED && <Tracking />}
          <App pageProps={pageProps} Component={Component} />
        </Provider>
      </ErrorBoundary>
    </NextIntlProvider>
  )
}

export default StoreApp
